import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, of, throwError } from 'rxjs';
import { RequestFailedError, fetchWithRetry } from '../api/request-retry';
import { INLINE_ICON_SVG } from './inline-icons.generated';

/**
 * An icon whose bytes never arrived - dropped connection, offline, blocked by
 * an extension - after every retry.
 *
 * Distinct from a `RequestFailedError` on purpose: the server never answered,
 * so nothing here is broken and there is nobody to tell. `IconComponent` uses
 * the distinction to pick a log level, which is what keeps a flaky mobile
 * connection out of Sentry.
 */
export class IconFetchError extends Error {
  constructor(
    public readonly url: string,
    cause: unknown
  ) {
    super(`Icon ${url} could not be fetched`, { cause });
    this.name = 'IconFetchError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class IconRegistry {
  private svgIconMap = new Map<string, SafeResourceUrl>();
  private svgContentCache = new Map<string, Promise<string>>();

  constructor(private domSanitizer: DomSanitizer) {}

  /**
   * Downloads a registered icon, once per URL for the life of the document.
   *
   * The first-screen set never gets that far: those SVGs are compiled into
   * the bundle (see inline-icons.generated.ts) and answered from memory. They
   * are navigation chrome, so they were six requests fired at bootstrap into
   * the LCP window, each costing a round trip - ~250 ms on a slow link - to
   * move well under a kilobyte. That also puts them out of reach of every
   * failure mode below, which is why the retry machinery is documented as
   * defending the long tail rather than the nav bar.
   *
   * Only a *resolved* download is remembered. Caching the rejection made one
   * dropped request permanent: the icon stayed blank in every component that
   * later asked for it, and every one of them logged an error, because nothing
   * ever went back to the network. That was the bulk of the "Failed to fetch"
   * Sentry issue this comment exists for - phones moving between wifi and
   * cellular, not a broken asset.
   *
   * `fetchWithRetry` supplies the other half: a dropped request is status 0,
   * which it retries with backoff, so the common case never reaches the caller
   * at all. It also rejects a non-OK response, which a bare `fetch().text()`
   * did not - stage answers a missing `/assets/icons/x.svg` with `index.html`
   * (its nginx has no asset location and falls through to `try_files`), and
   * that HTML was being written straight into the icon's `innerHTML`.
   */
  fetchSvgContent(url: string): Promise<string> {
    const inlined = INLINE_ICON_SVG[url];
    if (inlined) return Promise.resolve(inlined);

    const cached = this.svgContentCache.get(url);
    if (cached) return cached;

    const pending = fetchWithRetry(url, `icon ${url}`)
      .catch((error: unknown) => {
        // `fetchWithRetry` rethrows a bad status as a `RequestFailedError` and
        // a network failure as whatever `fetch` rejected with; only the second
        // is the visitor's connection rather than a broken asset.
        if (error instanceof RequestFailedError) throw error;
        throw new IconFetchError(url, error);
      })
      .then((response) => response.text())
      .then((svg) => {
        // A 200 that is not an SVG is the stage case above, and injecting it
        // would replace a missing icon with a whole HTML document.
        if (!svg.includes('<svg')) {
          throw new Error(`Not an SVG: ${url}`);
        }
        return svg;
      });

    this.svgContentCache.set(url, pending);
    pending.catch(() => {
      if (this.svgContentCache.get(url) === pending) {
        this.svgContentCache.delete(url);
      }
    });

    return pending;
  }

  /**
   * Registers an SVG icon by name
   * @param iconName Name to associate with the icon
   * @param url Safe resource URL for the SVG icon
   */
  addSvgIcon(iconName: string, url: SafeResourceUrl): void {
    this.svgIconMap.set(iconName, url);
  }

  /**
   * Gets an SVG icon by name
   * @param iconName Name of the icon to retrieve
   * @returns The SafeResourceUrl for the icon or undefined if not found
   */
  getIcon(iconName: string): SafeResourceUrl | undefined {
    return this.svgIconMap.get(iconName);
  }

  /**
   * Gets an SVG icon as an Observable (MatIconRegistry compatibility)
   * @param namespace Namespace (ignored in this implementation)
   * @param iconName Name of the icon to retrieve
   * @returns Observable of the icon or error if not found
   */
  getNamedSvgIcon(
    iconName: string,
    namespace: string = ''
  ): Observable<SafeResourceUrl> {
    // Remove colon prefix if present
    if (iconName.startsWith(':')) {
      iconName = iconName.substring(1);
    }

    const icon = this.svgIconMap.get(iconName);
    if (icon) {
      return of(icon);
    }

    return throwError(
      () =>
        new Error(
          `Unable to find icon with the name "${namespace ? namespace + ':' : ''}${iconName}"`
        )
    );
  }

  /**
   * Checks if an icon exists
   * @param iconName Name of the icon to check
   * @returns True if the icon exists, false otherwise
   */
  hasIcon(iconName: string): boolean {
    return this.svgIconMap.has(iconName);
  }

  /**
   * Removes an icon from the registry
   * @param iconName Name of the icon to remove
   */
  removeIcon(iconName: string): void {
    this.svgIconMap.delete(iconName);
  }
}
